import { template } from 'underscore';
import Sentry from '../../../FrontendBundle/Resources/js/sentry';

/**
 * A simple component class to handle the checking of browser support and display the result
 */
export default class BrowserFeaturesComponent {
    MIN_SCREEN_WIDTH = 1000;
    MIN_SCREEN_HEIGHT = 700;

    /**
     * Mapping for browser identifier to name
     *
     * @type {Object}
     * @private
     */
    browserNameMap = {
        and_chr: 'Chrome for Android',
        and_ff: 'Firefox for Android',
        and_qq: 'QQ Browser for Android',
        and_uc: 'UC Browser for Android',
        bb: 'Blackberry Browser',
        ie: 'Internet Explorer',
        ie_mob: 'Internet Explorer Mobile',
        ios_saf: 'iOS Safari',
        op_mini: 'Opera Mini',
        op_mob: 'Opera Mobile',
    };

    /**
     * Mapping for severity identifier to icon class
     *
     * @type {Object}
     * @private
     */
    severityIconMap = {
        danger: 'icon-remove-sign',
        warning: 'icon-warning-sign',
        success: 'icon-ok-sign',
    };

    /**
     * @param {jQuery} element jQuery element where to render the component
     * @param {UAParser} userAgent
     * @param {Object} messages
     */
    constructor(element, userAgent, messages) {
        this.$element = element;
        this.messages = messages;
        this.userAgent = userAgent;

        this._handleCollapsibleEvents();

        this.$header = this.$element.children('.header');
        this.$details = this.$element.children('.details');
    }

    /**
     * Check if browser is supported and render the result accordingly
     *
     * @param {string[]} supportedBrowsers
     * @param {Object} screenDimensions
     */
    render(supportedBrowsers, screenDimensions) {
        const result = this._checkSupport(supportedBrowsers, screenDimensions);
        this._renderResult(result, supportedBrowsers);
    }

    /**
     * Check if the current userAgent is supported and return a result
     *
     * @param {string[]} supportedBrowsers
     * @param {Object} screenDimensions
     *
     * @return {Object}
     * @private
     */
    _checkSupport(supportedBrowsers, screenDimensions) {
        let result = {
            message: this.messages.supported,
            showSupportedBrowsers: false,
            severity: 'success',
        };

        try {
            if (!this.userAgent.isSupported(supportedBrowsers)) {
                result = {
                    message: this.messages.notSupported,
                    showSupportedBrowsers: true,
                    severity: 'warning',
                };
            } else if (!this.userAgent.isUpToDate(supportedBrowsers)) {
                result = {
                    message: this.messages.upgradeable,
                    showSupportedBrowsers: false,
                    severity: 'warning',
                };
            } else if (screenDimensions && this._isScreenSizeSupported(screenDimensions)) {
                result = {
                    message: this.messages.lowResolution,
                    showSupportedBrowsers: false,
                    severity: 'warning',
                };
            }
        } catch (error) {
            result = {
                message: this.messages.notSupported,
                showSupportedBrowsers: true,
                severity: 'warning',
            };

            Sentry.captureException(error, {
                extra: {
                    info: 'Error in browser detection was caught and a valid result was returned.',
                    returnedResult: result,
                },
            });
        }

        return result;
    }

    /**
     * Check if the given screen dimensions are supported
     *
     * @param {Object} screenDimensions
     * @return {boolean}
     * @private
     */
    _isScreenSizeSupported(screenDimensions) {
        return screenDimensions.width < this.MIN_SCREEN_WIDTH || screenDimensions.height < this.MIN_SCREEN_HEIGHT;
    }

    /**
     * Render a result object to the DOM
     *
     * @param {Object} result
     * @param {string[]} supportedBrowsers
     * @private
     */
    _renderResult(result, supportedBrowsers) {
        const description = template(result.message.description)({
            useragent: this.userAgent.getBrowser().name,
            version: this.userAgent.getBrowser().version.split('.').slice(0, 2).join('.'),
        });

        let list = '';
        if (result.showSupportedBrowsers) {
            const listItems = this._getSupportedBrowserNames(supportedBrowsers).map(name => `<li>${name}</li>`);
            list = `<ul>${listItems.join('')}</ul>`;
        }

        this._reset();

        this.$header.addClass(`btn-${result.severity}`);
        this.$header.find('.status').addClass(this.severityIconMap[result.severity]);
        this.$header.find('.text').html(result.message.title);

        this.$details.find('.content').html(`<p>${description}</p>${list}`);
    }

    /**
     * Reset the component DOM
     *
     * @private
     */
    _reset() {
        this.$header.removeClass('btn-danger');
        this.$header.find('.status').removeClass('icon-remove-sign');
        if (!this.$element.is('.-rigid') && this.$details.collapse) this.$details.collapse('hide');
    }

    /**
     * Listen to collapsible events to add a custom class to the component
     *
     * @private
     */
    _handleCollapsibleEvents() {
        this.$element
            .on('show', () => this.$element.addClass('-open'))
            .on('hide', () => this.$element.removeClass('-open'));
    }

    /**
     * Return the browser name for the given browser identifier
     *
     * @param {string} shortName
     *
     * @return {string}
     * @private
     */
    _getBrowserName(shortName) {
        if (this.browserNameMap[shortName]) return this.browserNameMap[shortName];
        return shortName.charAt(0).toUpperCase() + shortName.slice(1);
    }

    /**
     * Return a list of supported browser names
     *
     * @param {string[]} supportedBrowsers
     *
     * @return {string[]}
     * @private
     */
    _getSupportedBrowserNames(supportedBrowsers) {
        const names = supportedBrowsers
            .map((identifier) => {
                const [, shortName, version] = identifier.match(/([a-z_]+) (.*)$/);
                if (shortName === 'ie') return `${this._getBrowserName(shortName)} ${version}`;
                return this._getBrowserName(shortName);
            });

        return this._makeUnique(names);
    }

    /**
     * Remove duplicates from array
     *
     * @param {Array.<*>} array
     *
     * @return {Array.<*>}
     * @private
     */
    _makeUnique(array) {
        return array.filter((name, index, list) => list.indexOf(name) === index);
    }
}
